import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage, useAuth, api } from '@/lib/context';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Lock, Unlock, Mail, Phone, Loader2, CheckCircle2, 
  CreditCard, AlertCircle, ExternalLink, User, Building2
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export const LockedContactCard = ({ 
  targetUserId, 
  targetUserName = 'User',
  targetUserRole = 'worker',
  jobId = null,
  companyName = null,
  onUnlocked = null 
}) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [unlocking, setUnlocking] = useState(false);
  const [status, setStatus] = useState({
    unlocked: false,
    price: 0,
    is_free: false,
    unlock_type: 'company_to_worker'
  });
  const [contact, setContact] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const checkUnlockStatus = useCallback(async () => {
    try {
      const res = await api.get(`/stripe/unlock/status?target_id=${targetUserId}&job_id=${jobId}`);
      setStatus(res.data);
      if (res.data.unlocked && res.data.contact) {
        setContact(res.data.contact);
      }
    } catch (error) {
      console.error('Failed to check unlock status:', error);
    } finally {
      setLoading(false);
    }
  }, [targetUserId, jobId]);

  useEffect(() => {
    if (targetUserId && user) {
      checkUnlockStatus();
    } else {
      setLoading(false);
    }
  }, [targetUserId, user, checkUnlockStatus]);

  const handleUnlock = async () => {
    if (!user) {
      toast.error(t('auth.loginRequired'));
      return;
    }

    // If free unlock available, use direct monetization endpoint
    if (status.is_free) {
      setUnlocking(true);
      try {
        const res = await api.unlockContact({
          target_user_id: targetUserId,
          unlock_type: status.unlock_type,
          context_id: jobId,
          context_type: jobId ? 'job' : null
        });
        
        setContact({
          email: res.data.email,
          phone: res.data.phone,
          name: targetUserName
        });
        setStatus({ ...status, unlocked: true });
        setShowConfirmDialog(false);
        
        toast.success(t('unlock.success'));
        
        if (onUnlocked) {
          onUnlocked(res.data);
        }
      } catch (error) {
        toast.error(error.response?.data?.detail || t('common.error'));
      } finally {
        setUnlocking(false);
      }
      return;
    }

    // Paid unlock - create Stripe checkout
    setUnlocking(true);
    try {
      const res = await api.post('/stripe/unlock/create-checkout', {
        target_user_id: targetUserId,
        job_id: jobId,
        unlock_type: status.unlock_type
      });
      
      // Check if already unlocked (prevent double payment)
      if (res.data.already_unlocked) {
        toast.info(t('unlock.alreadyUnlocked'));
        // Refresh unlock status
        await checkUnlockStatus();
        setShowConfirmDialog(false);
        setUnlocking(false);
        return;
      }
      
      // Check if user has free unlocks available
      if (res.data.has_free_unlocks) {
        toast.info(t('unlock.freeAvailable'));
        setStatus({ ...status, is_free: true });
        setShowConfirmDialog(false);
        setUnlocking(false);
        return;
      }
      
      // Redirect to Stripe checkout
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
      setUnlocking(false);
    }
  };

  // Not logged in - show login prompt
  if (!user) {
    return (
      <Card className="border-dashed">
        <CardContent className="pt-6">
          <div className="text-center space-y-3">
            <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mx-auto">
              <Lock className="h-6 w-6 text-muted-foreground" />
            </div>
            <div>
              <h4 className="font-medium">
                {t('unlock.contactLocked')}
              </h4>
              <p className="text-sm text-muted-foreground">
                {t('unlock.loginToUnlock')}
              </p>
            </div>
            <Button variant="outline" asChild>
              <a href="/login">{t('auth.login')}</a>
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="py-6">
          <div className="flex items-center justify-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm">{t('common.loading')}</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Already unlocked - show contact info
  if (status.unlocked && contact) {
    return (
      <Card className="bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2 text-green-700 dark:text-green-400">
            <Unlock className="h-4 w-4" />
            {t('unlock.contactUnlocked')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-2 text-sm">
            {targetUserRole === 'worker' ? (
              <User className="h-4 w-4 text-muted-foreground" />
            ) : (
              <Building2 className="h-4 w-4 text-muted-foreground" />
            )}
            <span className="font-medium">{contact.name || companyName || targetUserName}</span>
          </div>
          
          {contact.email && (
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-muted-foreground" />
              <a 
                href={`mailto:${contact.email}`} 
                className="text-sm text-brand hover:underline"
                data-testid="unlocked-email"
              >
                {contact.email}
              </a>
            </div>
          )}
          
          {contact.phone && (
            <div className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-muted-foreground" />
              <a 
                href={`tel:${contact.phone}`} 
                className="text-sm text-brand hover:underline"
                data-testid="unlocked-phone"
              >
                {contact.phone}
              </a>
            </div>
          )}
          
          {!contact.email && !contact.phone && (
            <p className="text-sm text-muted-foreground">
              {t('unlock.noContactInfo')}
            </p>
          )}
        </CardContent>
      </Card>
    );
  }

  // Not unlocked - show unlock button
  return (
    <>
      <Card className="border-dashed border-amber-300 dark:border-amber-700">
        <CardContent className="pt-6">
          <div className="text-center space-y-3">
            <div className="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mx-auto">
              <Lock className="h-6 w-6 text-amber-600" />
            </div>
            <div>
              <h4 className="font-medium">
                {t('unlock.contactLocked')}
              </h4>
              <p className="text-sm text-muted-foreground">
                {t('unlock.unlockToContact') || `Unlock to see ${targetUserName}'s contact details`}
              </p>
            </div>
            
            <Button 
              onClick={() => setShowConfirmDialog(true)}
              className="gap-2"
              data-testid="unlock-contact-btn"
            >
              <Unlock className="h-4 w-4" />
              {status.is_free ? (
                t('unlock.unlockFree')
              ) : (
                <>
                  {t('unlock.unlockFor')} €{status.price?.toFixed(2)}
                </>
              )}
            </Button>
            
            {status.is_free && (
              <Badge variant="secondary" className="mt-2">
                <CheckCircle2 className="h-3 w-3 mr-1" />
                {t('unlock.includedInSubscription')}
              </Badge>
            )}
            
            <p className="text-xs text-muted-foreground">
              {t('unlock.securePayment')}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Confirm Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Unlock className="h-5 w-5" />
              {t('unlock.confirmTitle')}
            </DialogTitle>
            <DialogDescription>
              {t('unlock.confirmDesc') || `You are about to unlock contact information for ${targetUserName}.`}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {status.is_free ? (
              <div className="bg-green-50 dark:bg-green-950/30 rounded-lg p-4 flex items-center gap-3">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                <div>
                  <p className="font-medium text-green-700 dark:text-green-400">
                    {t('unlock.freeUnlock')}
                  </p>
                  <p className="text-sm text-green-600 dark:text-green-500">
                    {t('unlock.freeUnlockDesc')}
                  </p>
                </div>
              </div>
            ) : (
              <div className="bg-amber-50 dark:bg-amber-950/30 rounded-lg p-4 flex items-center gap-3">
                <CreditCard className="h-5 w-5 text-amber-600" />
                <div>
                  <p className="font-medium text-amber-700 dark:text-amber-400">
                    {t('unlock.paidUnlock')}
                  </p>
                  <p className="text-sm text-amber-600 dark:text-amber-500">
                    {t('unlock.willCharge')} €{status.price?.toFixed(2)}
                  </p>
                  <p className="text-xs text-amber-600/70 mt-1">
                    {t('unlock.stripeSecure')}
                  </p>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleUnlock} disabled={unlocking}>
              {unlocking ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {status.is_free ? (t('unlock.unlocking')) : (t('payments.redirecting'))}
                </>
              ) : (
                <>
                  <Unlock className="mr-2 h-4 w-4" />
                  {t('unlock.confirmUnlock')}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default LockedContactCard;
